/**
 * The agent's browser in the chat (slice 6f): a picture of the page after each
 * browser step. While a turn runs, the newest is under the progress line (F1)
 * and folds into a row once the agent moves on (F5, F6); a finished turn's
 * computer card shows its last page, or a step's own when it's pressed (F2, F3).
 * Where there's none, one quiet line says why (F4).
 */
import { BrowserPicture, BrowserUsed, NoPicture } from "@crew/ui/components/work-fold";
import { useEffect, useState } from "react";
import type { FoldStep } from "../../../worker/domain/step-copy";
import { duration } from "../activity/activity-view";
import { useNow } from "../setup/hooks";
import { frameUrl, type ProgressFrame, type Turn } from "./api";
import { stepCount } from "./work";

/** A page's host for a caption, `www.` dropped. */
function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/** "3s ago", "2m ago". */
function ago(at: string, now: number): string {
  const s = Math.max(0, Math.round((now - Date.parse(at)) / 1000));
  return s < 60 ? `${s}s ago` : `${Math.floor(s / 60)}m ago`;
}

/**
 * The newest frame whose picture has loaded: a new one replaces it only once
 * its picture has (spec §4), so the card never flashes empty between steps.
 * `failed`: the newest one's picture couldn't be loaded.
 */
function useLoadedFrame(turn: Turn, frame: ProgressFrame) {
  const [shown, setShown] = useState<ProgressFrame | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const callId = frame.call_id;
  useEffect(() => {
    if (!callId) return;
    const img = new Image();
    img.onload = () => setShown(frame);
    img.onerror = () => setFailed(callId);
    img.src = frameUrl(turn.thread_id, turn.id, callId);
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [turn.thread_id, turn.id, callId, frame]);
  return { shown, failed: callId !== null && failed === callId };
}

/**
 * F1: the newest picture while the agent uses the browser, "After step 4 · 3s
 * ago" on it. A sign-in page has none, and says so, as does one that won't load.
 */
export function LiveBrowser({ turn, frame }: { turn: Turn; frame: ProgressFrame }) {
  const now = useNow();
  const { shown, failed } = useLoadedFrame(turn, frame);
  if (frame.sign_in || failed) {
    return (
      <div className="w-[560px] max-w-full">
        <NoPicture reason={frame.sign_in ? "sign_in" : "unavailable"} />
      </div>
    );
  }
  const current = shown ?? (frame.call_id ? frame : null);
  if (!current?.call_id) return null;
  return (
    <BrowserPicture
      className="w-[560px] max-w-full"
      src={frameUrl(turn.thread_id, turn.id, current.call_id)}
      site={hostOf(current.url)}
      title={current.title}
      badge={`After step ${current.steps} · ${ago(current.at, now)}`}
    />
  );
}

/**
 * F5, F6: the browser the turn used and moved on from, folded to a row with
 * its last page; it opens to the picture, or to why there's none.
 */
export function MovedOnBrowser({ turn, frame }: { turn: Turn; frame: ProgressFrame }) {
  const src = frame.call_id ? frameUrl(turn.thread_id, turn.id, frame.call_id) : null;
  const meta = [frame.site, stepCount(frame.steps), duration(Math.max(frame.ms, 100))]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="w-[360px] max-w-full">
      <BrowserUsed thumb={src} meta={meta}>
        {src ? (
          <BrowserPicture src={src} site={hostOf(frame.url)} title={frame.title} />
        ) : (
          <NoPicture reason={frame.sign_in ? "sign_in" : "none"} />
        )}
      </BrowserUsed>
    </div>
  );
}

type FramedStep = Pick<FoldStep, "call_id" | "url" | "title" | "frame">;

/**
 * F2, F3: a finished turn's picture in its computer card: the step pressed,
 * else the last one with a picture. Null for a turn from before pictures.
 */
export function FinishedPicture({
  threadId,
  turnId,
  steps,
  selected,
}: {
  threadId: string;
  turnId: string;
  steps: FramedStep[];
  selected: string | null;
}) {
  const [gone, setGone] = useState<ReadonlySet<string>>(new Set());
  const shown =
    steps.find((s) => s.call_id === selected && s.frame === "saved") ??
    steps.filter((s) => s.frame === "saved").at(-1);
  if (!shown) {
    return steps.some((s) => s.frame === "sign_in") ? <NoPicture reason="sign_in" /> : null;
  }
  if (gone.has(shown.call_id)) return <NoPicture reason="gone" />;
  const n = steps.indexOf(shown) + 1;
  return (
    <BrowserPicture
      src={frameUrl(threadId, turnId, shown.call_id)}
      site={hostOf(shown.url)}
      title={shown.title}
      trailing={`Step ${n}`}
      onError={() => setGone((was) => new Set(was).add(shown.call_id))}
    />
  );
}
